import { type Item, tagSchema } from '@hugo/core'

/**
 * Tags group items by area: family, house, a side project (decision 97). A personal tag keeps
 * its items out of the working hours hugo plans. These helpers only show and filter; the server
 * decides what is personal for planning.
 */

/** "#family". */
export function tagLabel(tag: string): string {
  return `#${tag}`
}

/** Whether an item is personal: one of its tags is one of the user's personal tags. */
export function isPersonalItem(
  item: Pick<Item, 'tags'>,
  personalTags: readonly string[] | undefined,
): boolean {
  return personalTags !== undefined && item.tags.some((tag) => personalTags.includes(tag))
}

/** The tags a row or card shows: two, and how many more. */
export function shownTags(tags: readonly string[], max = 2): { shown: string[]; more: number } {
  return { shown: tags.slice(0, max), more: Math.max(0, tags.length - max) }
}

/** The personal tags with one switched on or off; the others keep their order. */
export function togglePersonal(personal: readonly string[], tag: string, on: boolean): string[] {
  const rest = personal.filter((name) => name !== tag)
  return on ? [...rest, tag] : rest
}

/** A tag filter from the address (`?tag=family`); anything else shows everything. */
export const tagFilterSchema = tagSchema.optional().catch(undefined)

/** Whether an item passes a tag filter (none: every item). */
export function hasTag(item: Pick<Item, 'tags'>, tag: string | undefined): boolean {
  return tag === undefined || item.tags.includes(tag)
}

/** The tags on some items, by name, each once (for a filter). */
export function tagsOf(items: readonly Pick<Item, 'tags'>[]): string[] {
  return [...new Set(items.flatMap((item) => item.tags))].sort()
}

/** A tag typed in the details or Settings, normalized like the server does; null if not a tag. */
export function parseTag(input: string): string | null {
  const parsed = tagSchema.safeParse(input)
  return parsed.success ? parsed.data : null
}
